import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Input, Modal, Form, Select, Badge, Space } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import toast from 'react-hot-toast';
import { getApiEndpoint, authFetch } from '../../../config/apiBase';

const MigrateTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchSenderId, setSearchSenderId] = useState('');
  const [searchTemplateName, setSearchTemplateName] = useState('');
  const [searchEntityId, setSearchEntityId] = useState('');
  const [searchTemplateId, setSearchTemplateId] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [form] = Form.useForm();

  const { TextArea } = Input;

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const url = getApiEndpoint('/migration/templates');
      const response = await authFetch(url);
      if (response.ok) {
        const result = await response.json();
        setTemplates(result.data || []);
      }
    } catch (error) {
      toast.error('Failed to fetch templates');
    }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const response = await authFetch(getApiEndpoint('/migration/users'));
      if (response.ok) {
        const result = await response.json();
        const usersArr = result.data || result || [];
        setAllUsers(Array.isArray(usersArr) ? usersArr.map((u) => u.username) : []);
      }
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  // Get unique sender IDs from templates
  const getUniqueSenderIds = () => {
    const senderIds = [...new Set(templates.map(t => t.sender_id).filter(Boolean))];
    return senderIds.sort();
  };

  // Get unique template names from templates
  const getUniqueTemplateNames = () => {
    const templateNames = [...new Set(templates.map(t => t.template_name).filter(Boolean))];
    return templateNames.sort();
  };

  // Get unique entity IDs from templates
  const getUniqueEntityIds = () => {
    const entityIds = [...new Set(templates.map(t => t.entity_id).filter(Boolean))];
    return entityIds.sort();
  };

  // Get unique template IDs from templates
  const getUniqueTemplateIds = () => {
    const templateIds = [...new Set(templates.map(t => t.template_id).filter(Boolean))];
    return templateIds.sort();
  };

  useEffect(() => {
    fetchTemplates();
    fetchUsers();
  }, []);

  const handleSearch = () => {
    fetchTemplates();
  };

  const handleClearFilters = () => {
    setSearchUsername('');
    setSearchSenderId('');
    setSearchTemplateName('');
    setSearchEntityId('');
    setSearchTemplateId('');
    fetchTemplates();
  };

  // Filter templates based on search criteria
  const filteredTemplates = templates.filter(template => {
    const s = (v) => (v || '').toString().toLowerCase();
    const usernameMatch = !searchUsername || s(template.username).includes(searchUsername.toLowerCase());
    const senderIdMatch = !searchSenderId || s(template.sender_id).includes(searchSenderId.toLowerCase());
    const templateNameMatch = !searchTemplateName || s(template.template_name).includes(searchTemplateName.toLowerCase());
    const entityIdMatch = !searchEntityId || s(template.entity_id).includes(searchEntityId.toLowerCase());
    const templateIdMatch = !searchTemplateId || s(template.template_id).includes(searchTemplateId.toLowerCase());
    return usernameMatch && senderIdMatch && templateNameMatch && entityIdMatch && templateIdMatch;
  });

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowViewDialog(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    form.setFieldsValue(template);
    setShowEditDialog(true);
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;

    const values = await form.validateFields();
    setLoading(true);
    try {
      const response = await authFetch(getApiEndpoint(`/migration/templates/${editingTemplate.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editingTemplate, ...values }),
      });

      if (response.ok) {
        toast.success('Template updated successfully');
        setShowEditDialog(false);
        setEditingTemplate(null);
        form.resetFields();
        fetchTemplates();
      } else {
        toast.error('Failed to update template');
      }
    } catch (error) {
      toast.error('Failed to update template');
    }
    setLoading(false);
  };

  const handleDeleteTemplate = async (id) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this template?',
      onOk: async () => {
        try {
          const response = await authFetch(getApiEndpoint(`/migration/templates/${id}`), {
            method: 'DELETE',
          });

          if (response.ok) {
            toast.success('Template deleted successfully');
            fetchTemplates();
          } else {
            toast.error('Failed to delete template');
          }
        } catch (error) {
          toast.error('Failed to delete template');
        }
      },
    });
  };

  const formatRawPayload = (payload) => {
    try {
      const parsed = JSON.parse(payload);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return payload;
    }
  };

  const columns = [
    {
      title: 'No',
      key: 'no',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Sender ID',
      dataIndex: 'sender_id',
      key: 'sender_id',
      render: (text) => {
        if (!text) return '-';
        
        const senderIds = text.split(',').map(id => id.trim());
        const firstId = senderIds[0];
        
        if (senderIds.length > 1) {
          return `${firstId} (${senderIds.length})`;
        }
        
        return firstId;
      },
    },
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
    },
    {
      title: 'Entity ID',
      dataIndex: 'entity_id',
      key: 'entity_id',
      render: (text) => <span className="font-mono text-sm">{text}</span>,
    },
    {
      title: 'Template ID',
      dataIndex: 'template_id',
      key: 'template_id',
      render: (text) => <span className="font-mono text-sm">{text}</span>,
    },
    {
      title: 'Unicode',
      dataIndex: 'is_unicode',
      key: 'is_unicode',
      render: (isUnicode) => (
        <Badge status={isUnicode ? 'success' : 'default'} text={isUnicode ? 'Yes' : 'No'} />
      ),
    },
    {
      title: 'Tested',
      dataIndex: 'is_tested',
      key: 'is_tested',
      render: (isTested) => (
        <Badge status={isTested ? 'success' : 'default'} text={isTested ? 'Yes' : 'No'} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleViewTemplate(record)}>
            <EyeOutlined />
          </Button>
          <Button size="small" onClick={() => handleEditTemplate(record)}>
            <EditOutlined />
          </Button>
          <Button size="small" danger onClick={() => handleDeleteTemplate(record.id)}>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-3xl font-bold">Migrate Templates</h1>
        <Button onClick={() => fetchTemplates()} icon={<ReloadOutlined />}>
          Refresh
        </Button>
      </div>

      <Card
        title="Migrated Templates"
        extra={
          <Space wrap>
            <Select
              placeholder="Search by username..."
              value={searchUsername || undefined}
              onChange={(value) => setSearchUsername(value || '')}
              style={{ width: 150 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {allUsers.map(user => (
                <Select.Option key={user} value={user}>{user}</Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Search by sender ID..."
              value={searchSenderId || undefined}
              onChange={(value) => setSearchSenderId(value || '')}
              style={{ width: 150 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getUniqueSenderIds().map(senderId => (
                <Select.Option key={senderId} value={senderId}>{senderId}</Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Search by template name..."
              value={searchTemplateName || undefined}
              onChange={(value) => setSearchTemplateName(value || '')}
              style={{ width: 180 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getUniqueTemplateNames().map(templateName => (
                <Select.Option key={templateName} value={templateName}>{templateName}</Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Search by entity ID..."
              value={searchEntityId || undefined}
              onChange={(value) => setSearchEntityId(value || '')}
              style={{ width: 150 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getUniqueEntityIds().map(entityId => (
                <Select.Option key={entityId} value={entityId}>{entityId}</Select.Option>
              ))}
            </Select>
            <Select
              placeholder="Search by template ID..."
              value={searchTemplateId || undefined}
              onChange={(value) => setSearchTemplateId(value || '')}
              style={{ width: 150 }}
              allowClear
              showSearch
              filterOption={(input, option) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getUniqueTemplateIds().map(templateId => (
                <Select.Option key={templateId} value={templateId}>{templateId}</Select.Option>
              ))}
            </Select>
            <Button onClick={handleClearFilters}>
              Clear
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={Array.isArray(filteredTemplates) ? filteredTemplates : []}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Template Details"
        open={showViewDialog}
        onCancel={() => setShowViewDialog(false)}
        footer={null}
        width={800}
      >
        {selectedTemplate && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <strong>Username:</strong>
                <p>{selectedTemplate.username}</p>
              </div>
              <div>
                <strong>Sender ID:</strong>
                <p>{selectedTemplate.sender_id}</p>
              </div>
              <div>
                <strong>Template Name:</strong>
                <p>{selectedTemplate.template_name}</p>
              </div>
              <div>
                <strong>Entity ID:</strong>
                <p className="font-mono">{selectedTemplate.entity_id}</p>
              </div>
              <div>
                <strong>Template ID:</strong>
                <p className="font-mono">{selectedTemplate.template_id}</p>
              </div>
              <div>
                <strong>Created At:</strong>
                <p>{new Date(selectedTemplate.created_at).toLocaleString()}</p>
              </div>
            </div>
            <div>
              <strong>Template Content:</strong>
              <TextArea
                value={selectedTemplate.template_content}
                readOnly
                rows={4}
                className="mt-2"
              />
            </div>
            <div>
              <strong>Raw Payload:</strong>
              <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-64">
                {formatRawPayload(selectedTemplate.raw_payload)}
              </pre>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="Edit Template"
        open={showEditDialog}
        onOk={handleUpdateTemplate}
        onCancel={() => {
          setShowEditDialog(false);
          setEditingTemplate(null);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        {editingTemplate && (
          <Form form={form} layout="vertical" initialValues={editingTemplate}>
            <Form.Item
              name="username"
              label="Username"
              rules={[{ required: true, message: 'Please select username' }]}
            >
              <Select>
                {allUsers.map((user) => (
                  <Select.Option key={user} value={user}>
                    {user}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="sender_id"
              label="Sender ID"
              rules={[{ required: true, message: 'Please enter sender ID' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="template_name"
              label="Template Name"
              rules={[{ required: true, message: 'Please enter template name' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="template_content"
              label="Template Content"
              rules={[{ required: true, message: 'Please enter template content' }]}
            >
              <TextArea rows={4} />
            </Form.Item>
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="entity_id"
                label="Entity ID"
                rules={[{ required: true, message: 'Please enter entity ID' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="template_id"
                label="Template ID"
                rules={[{ required: true, message: 'Please enter template ID' }]}
              >
                <Input />
              </Form.Item>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Form.Item name="is_unicode" label="Unicode">
                <Select>
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="is_tested" label="Tested">
                <Select>
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              </Form.Item>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default MigrateTemplatesPage;
